import React, {useRef} from "react";
import {Box, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {InformationCircleIcon} from "@hugeicons/core-free-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {useSyncedScroll} from "../../hooks/useSyncedScroll";
import {MARKDOWN_PREVIEW_SX} from "./markdownPreviewSx";

const kickerRowSx = {px: 2, pt: 1.5, pb: 0.75};
const kickerSx = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "#8A8AA0", textTransform: "uppercase",
};

const MarkdownCheatsheet = () => (
  <Tooltip
    title={
      <Stack gap={0.3} sx={{py: 0.5}}>
        <Typography variant="caption"># Heading 1 &nbsp;&nbsp;## Heading 2</Typography>
        <Typography variant="caption">**bold** &nbsp;&nbsp;*italic*</Typography>
        <Typography variant="caption">`inline code`</Typography>
        <Typography variant="caption">- list item &nbsp;&nbsp;1. numbered</Typography>
        <Typography variant="caption">[label](url)</Typography>
        <Typography variant="caption">&gt; blockquote</Typography>
        <Typography variant="caption">--- (horizontal rule)</Typography>
      </Stack>
    }
    arrow
    placement="right"
  >
    <HugeiconsIcon icon={InformationCircleIcon} size={13} style={{cursor: "help", opacity: 0.6}}/>
  </Tooltip>
);

// Plain-text editor on the left, synced-scroll markdown preview on the right, fixed 50/50 split.
// For editors that need {{variable}} autocomplete, see MarkdownEditorModal instead.
export const SplitMarkdownEditor = ({
  value, onChange, minHeight = 320, placeholder, ariaLabel = "Document content", bordered = true,
}) => {
  const editorRef = useRef(null);
  const previewRef = useRef(null);
  const syncScroll = useSyncedScroll();

  const handleEditorScroll = () => syncScroll(editorRef.current, previewRef.current);
  const handlePreviewScroll = () => syncScroll(previewRef.current, editorRef.current);

  return (
    <Stack direction="row" sx={{
      height: "100%", minHeight, overflow: "hidden",
      ...(bordered ? {bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px"} : {}),
    }}>
      <Box sx={{width: "50%", flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden"}}>
        <Stack direction="row" alignItems="center" gap={0.5} sx={kickerRowSx}>
          <Typography sx={kickerSx}>Plain text</Typography>
          <MarkdownCheatsheet/>
        </Stack>
        <Box
          component="textarea"
          ref={editorRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={handleEditorScroll}
          placeholder={placeholder}
          aria-label={ariaLabel}
          sx={{
            flex: 1, minHeight: 0, width: "100%", resize: "none", border: "none", outline: "none",
            bgcolor: "transparent", fontSize: 13, color: "#23264D", lineHeight: 1.8,
            fontFamily: "inherit", px: 2, pb: 2, boxSizing: "border-box",
          }}
        />
      </Box>
      <Box sx={{width: "50%", flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#FAFAFD"}}>
        <Typography sx={{...kickerSx, ...kickerRowSx}}>Markdown preview</Typography>
        <Box
          ref={previewRef}
          onScroll={handlePreviewScroll}
          sx={{
            flex: 1, minHeight: 0, overflow: "auto", px: 2, pb: 2,
            fontSize: 13, color: "#23264D", lineHeight: 1.8,
            ...MARKDOWN_PREVIEW_SX,
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {value || "*Nothing to preview*"}
          </ReactMarkdown>
        </Box>
      </Box>
    </Stack>
  );
};

export default SplitMarkdownEditor;
